import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { openCleoCustomerUpdate } from "@/components/cleo/useCleoDrawerState";

type Props = {
  customerId: number;
  proposalId?: number | null;
  customerContact?: string | null;
  customerFirstName: string;
  lastContactAt: Date | string | null | undefined;
  autoStart?: boolean;
  onCompleted?: () => void;
  canMutate?: boolean;
};

export function CleoCustomerCardPanel({
  customerId,
  proposalId,
  customerFirstName,
  autoStart,
  onCompleted,
  canMutate = true,
}: Props) {
  const [draft, setDraft] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  const [undoId, setUndoId] = useState<number | null>(null);
  const utils = trpc.useUtils();
  const ctxQ = trpc.cleoCustomerUpdate.getContext.useQuery({ customerId }, { enabled: customerId > 0 });
  const nba = trpc.proposals.getNextBestAction.useQuery(
    { customerId, proposalId: proposalId ?? undefined },
    { enabled: customerId > 0 },
  );
  const reply = trpc.cleoCustomerUpdate.reply.useMutation({
    onSuccess: (res) => {
      if (res.clarification) {
        toast.message(res.clarification);
        return;
      }
      setSaved(res.assistantMessage);
      setUndoId(res.conversationEventId);
      setDraft("");
      toast.success("Internal update saved");
      void ctxQ.refetch();
      void nba.refetch();
      void utils.proposals.getCustomerSalesTimeline.invalidate({ customerId });
      void utils.cleoCustomerUpdate.listVisibleWork.invalidate();
      onCompleted?.();
    },
    onError: (e) => toast.error(e.message || "Could not save update"),
  });
  const undo = trpc.cleoCustomerUpdate.undo.useMutation({
    onSuccess: () => {
      toast.success("Undid reminder");
      setUndoId(null);
      void utils.proposals.getCustomerSalesTimeline.invalidate({ customerId });
    },
    onError: (e) => toast.error(e.message || "Undo failed"),
  });
  const setReminder = trpc.proposals.setCustomerReminder.useMutation({
    onSuccess: async () => {
      toast.success("Follow-up reminder set");
      void utils.cleoCustomerUpdate.listVisibleWork.invalidate();
      await Promise.all([
        ctxQ.refetch(),
        utils.proposals.listCustomerReminders.invalidate({ customerId }),
        utils.proposals.getCustomerSalesTimeline.invalidate({ customerId }),
      ]);
    },
    onError: (e) => toast.error(e.message || "Could not set reminder"),
  });

  useEffect(() => {
    if (!autoStart || !canMutate) return;
    openCleoCustomerUpdate(customerId);
  }, [autoStart, customerId, canMutate]);

  const grounded = ctxQ.data;
  return (
    <Card className="border-[#1A2744]/15 bg-[#1A2744]/[0.03] h-fit" data-testid="cleo-customer-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-[#1A2744]">
          <Sparkles className="h-4 w-4 text-[#C8A96A]" />
          Cleo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-[#1A2744]/90">
        <div className="space-y-2 rounded-md border border-[#1A2744]/15 bg-white/80 p-2.5">
          <p className="text-xs font-semibold uppercase tracking-wide">Update status</p>
          {canMutate ? (
            <>
          <textarea
            className="w-full rounded border px-2 py-1 text-xs"
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`What is the latest update for ${customerFirstName || "this customer"}?`}
            data-testid="cleo-customer-update-input"
          />
          <Button
            type="button"
            size="sm"
            disabled={!draft.trim() || reply.isPending}
            onClick={() =>
              reply.mutate({
                customerId,
                proposalId: proposalId ?? undefined,
                message: draft.trim(),
              })
            }
          >
            Update status
          </Button>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">Read-only. The assigned PIC can record the next update.</p>
          )}
        </div>
        <div className="rounded-md border border-[#1A2744]/15 bg-white/80 p-2.5">
          <p className="text-xs uppercase tracking-wide font-semibold">Last action</p>
          <p className="mt-1 text-sm" data-testid="cleo-last-action">
            {grounded?.lastAction ?? "No customer interaction has been recorded yet."}
          </p>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5">
          <p className="text-xs uppercase tracking-wide font-semibold text-amber-900">Suggested action</p>
          <p className="mt-1 text-sm text-amber-950" data-testid="cleo-suggested-action">
            {grounded?.recommendedAction ?? nba.data?.reason ?? "Record the latest customer update and agree the next follow-up."}
          </p>
          {grounded?.suggestedReminderDateSgt && canMutate ? (
            <Button
              type="button"
              size="sm"
              className="mt-2"
              disabled={setReminder.isPending}
              onClick={() =>
                setReminder.mutate({
                  customerId,
                  proposalId: proposalId ?? undefined,
                  date: grounded.suggestedReminderDateSgt!,
                  note: grounded.suggestedReminderNote ?? undefined,
                  confirmed: true,
                })
              }
            >
              Set reminder for {grounded.suggestedReminderDateSgt}
            </Button>
          ) : null}
        </div>
        {saved ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-xs" data-testid="cleo-saved-result">
            <p>{saved}</p>
            {undoId ? (
              <Button type="button" size="sm" variant="ghost" className="mt-2 h-7 px-2" onClick={() => undo.mutate({ conversationEventId: undoId })}>
                Undo
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
